import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CareersForm } from "@/components/forms";
import {
  heroVariant,
  staggerContainer,
  itemVariant,
  viewportOnce,
  sectionVariant,
} from "@/lib/motion";

const positions = [
  { title: "Performance Marketing Manager", department: "Growth", location: "Remote", type: "Full-time" },
  { title: "Senior SEO Strategist", department: "SEO", location: "Remote", type: "Full-time" },
  { title: "CRO Specialist", department: "Optimization", location: "Remote", type: "Full-time" },
  { title: "Analytics Engineer", department: "Data", location: "Remote", type: "Full-time" },
];

const values = [
  {
    title: "Systems thinking",
    description: "We look for people who see the bigger picture and understand how pieces connect.",
  },
  {
    title: "Intellectual honesty",
    description: "We value truth over comfort. Admitting what we do not know is how we learn.",
  },
  {
    title: "Craft over speed",
    description: "Quality work takes time. We prioritize getting it right over getting it fast.",
  },
  {
    title: "Calm under pressure",
    description: "Our best work happens when we are focused, not frantic.",
  },
];

const Career = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="pt-28 pb-10 md:pt-36 md:pb-16 lg:pt-44 lg:pb-20">
        <div className="container-wide">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={staggerContainer} 
            className="max-w-3xl"
          >
            <motion.span 
              variants={heroVariant} 
              className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block"
            >
              Careers
            </motion.span>
            <motion.h1 
              variants={heroVariant} 
              className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground mb-6 leading-[1.1]"
            >
              Build growth systems that matter.
            </motion.h1>
            <motion.p 
              variants={heroVariant} 
              className="text-lg md:text-xl text-muted-foreground leading-relaxed"
            >
              We are looking for people who think in systems, care about craft, 
              and want to do meaningful work with ambitious companies.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-16 md:py-20 border-t border-border">
        <div className="container-wide">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={sectionVariant}
            className="mb-12"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
              Who We Are
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
              What we look for.
            </h2>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {values.map((value) => (
              <motion.div 
                key={value.title}
                variants={itemVariant}
                className="py-6 border-t border-border"
              >
                <h3 className="text-lg font-medium text-foreground mb-2">{value.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* OPEN POSITIONS */}
      <section className="py-16 md:py-20 border-t border-border bg-muted/30">
        <div className="container-wide">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={sectionVariant}
            className="mb-12"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
              Opportunities
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
              Open positions.
            </h2>
          </motion.div>

          <motion.div 
            className="space-y-0" 
            initial="hidden" 
            whileInView="visible" 
            viewport={viewportOnce} 
            variants={staggerContainer}
          >
            {positions.map((position) => (
              <motion.div 
                key={position.title} 
                variants={itemVariant} 
                className="grid md:grid-cols-12 gap-4 py-6 border-t border-border"
              >
                <div className="md:col-span-5">
                  <h3 className="text-lg font-medium text-foreground">{position.title}</h3>
                </div>
                <div className="md:col-span-3">
                  <span className="text-sm text-muted-foreground">{position.department}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-sm text-muted-foreground">{position.location}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-sm text-muted-foreground">{position.type}</span>
                </div>
              </motion.div>
            ))}
            <div className="border-t border-border" />
          </motion.div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section className="py-16 md:py-20 lg:py-24 border-t border-border">
        <div className="container-wide">
          <div className="max-w-2xl">
            <CareersForm 
              title="Apply to join us"
              description="We are always looking for talented people who think in systems. Tell us about yourself."
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Career;
